import classNames from 'classnames'
import * as React from 'react'
import { Tooltip as RechartsTooltip } from 'recharts'

import { ChartWrapperContext } from './ChartWrapper'
import { CursorLine } from './Cursor'
import { CHART_ITEM_COLORS, ChartItemColorType } from './constants'

type TooltipPayload<PayloadType, ValueType> = {
  color?: string
  name?: string | number
  value?: ValueType
  unit?: React.ReactNode
  dataKey?: string | number
  payload?: PayloadType
  chartType?: string
  className?: string
}

type TooltipValueFormatter<ValueType, PayloadType> = (
  value: ValueType,
  extra: {
    dataKey: string
    payload?: PayloadType & { dataKey: string }
    fullTooltipPayload: Array<TooltipPayload<PayloadType, any>>
  }
) => React.ReactNode

interface TooltipRowProps<ValueType, PayloadType> {
  /**
   * When active the row is more prominent
   */
  isActive?: boolean

  /**
   * Value getter key
   */
  dataKey: string

  /**
   * Whether or not the `dataKey` prop represents data visible as series on the chart
   * If false, the value will be parsed from the payload of the first series
   *
   * @default true
   */
  isChartSeriesDataKey?: boolean

  label: React.ReactNode | TooltipValueFormatter<ValueType, PayloadType>
  labelClass?: string

  formatValue?: TooltipValueFormatter<ValueType, PayloadType>
  valueClass?: string

  /**
   * Color or element
   */
  indicator?: ChartItemColorType | JSX.Element
  /**
   * Additional classes on indicator wrapper
   */
  indicatorClass?: string

  /**
   * Set by parent component do not set manually
   */
  dataKeyPayload?: any
  fullTooltipPayload?: any
}

export function TooltipRow<ValueType, PayloadType = unknown>({
  isActive,
  indicator,
  label,
  dataKey,
  dataKeyPayload,
  fullTooltipPayload,
  formatValue,
  indicatorClass,
  labelClass,
  valueClass,
  isChartSeriesDataKey = true,
}: TooltipRowProps<ValueType, PayloadType>) {
  const valueToUse = isChartSeriesDataKey ? dataKeyPayload?.value : fullTooltipPayload?.[0]?.payload?.[dataKey]
  const formatterPayload = isChartSeriesDataKey ? (dataKeyPayload ?? {}) : fullTooltipPayload?.[0]?.payload
  const formatterExtraParams = { dataKey, payload: formatterPayload, fullTooltipPayload }

  const formattedLabel = typeof label == 'function' ? label(valueToUse, formatterExtraParams) : label
  const formattedValue = formatValue?.(valueToUse, formatterExtraParams) ?? valueToUse

  return (
    <>
      <div className={classNames(indicatorClass, { 'mr-2': !!indicator })}>
        {!indicator || React.isValidElement(indicator) ? (
          indicator
        ) : (
          <div
            className={classNames(
              'relative -top-px h-1.5 w-1.5 rounded-full bg-current',
              CHART_ITEM_COLORS[indicator as ChartItemColorType] ?? CHART_ITEM_COLORS.blue
            )}
          />
        )}
      </div>
      <div
        className={classNames('label-with-bold-weight', labelClass, { 'text-default font-medium': isActive })}
        data-text={formattedLabel}
      >
        {formattedLabel}
      </div>
      <div className={classNames(valueClass, 'ml-2 text-right tabular-nums text-default')}>{formattedValue}</div>
    </>
  )
}

interface TooltipInnerProps<ValueType, PayloadType> extends React.ComponentProps<typeof RechartsTooltip> {
  className?: string
  showLabel?: boolean
  formatLabel?: (value: ValueType, payload?: Array<TooltipPayload<PayloadType, ValueType>>) => React.ReactNode
  formatValue?: TooltipValueFormatter<ValueType, PayloadType>
  translucent?: boolean
}

export function TooltipInner<ValueType, PayloadType>({
  payload,
  label,
  formatLabel,
  formatValue,
  showLabel = !!label,
  translucent,
  ...props
}: React.PropsWithChildren<TooltipInnerProps<ValueType, PayloadType>>) {
  const { isTooltipActive, activeKey } = React.useContext(ChartWrapperContext)

  return (
    <div
      className={classNames(
        props.className,
        'rounded-md border border-secondary bg-primary px-3.5 pb-2.5 pt-3.5 text-left font-sans text-2xs leading-4 text-muted-2 shadow-md',
        { 'opacity-0': !isTooltipActive, 'bg-opacity-70 backdrop-blur-sm': translucent }
      )}
    >
      {showLabel && (
        <div className="mb-1.5 text-sm text-default font-medium dark:text-gray-200">
          {formatLabel?.(label, payload as any) ?? label}
        </div>
      )}

      <div className="grid grid-cols-[max-content,1fr,max-content] items-baseline gap-y-2">
        {React.Children.map(props.children, (child) => {
          if (!React.isValidElement(child) || child.type !== TooltipRow) return child

          const dataKeyPayload = payload?.find((payload) => payload.dataKey === child.props.dataKey)

          return React.cloneElement(child, {
            isActive: child.props.isActive ?? activeKey === child.props.dataKey,
            dataKeyPayload,
            fullTooltipPayload: payload,
            formatValue: child.props.formatValue ?? formatValue,
          } as TooltipRowProps<unknown, unknown>)
        })}
      </div>
    </div>
  )
}

export class Tooltip<ValueType, PayloadType> extends React.Component<
  React.PropsWithChildren<TooltipInnerProps<ValueType, PayloadType>>
> {
  static displayName = RechartsTooltip.displayName

  static defaultProps = {
    ...RechartsTooltip.defaultProps,
    offset: 40,
    position: { y: 4 },
    isAnimationActive: false,
    wrapperStyle: { outline: 'none', zIndex: 10 },
    className: 'min-w-70',
    cursor: <CursorLine />,
  }

  render() {
    const { className, showLabel, formatLabel: formatTitle, formatValue, translucent, children, ...props } = this.props

    return (
      <RechartsTooltip
        content={
          <TooltipInner<ValueType, PayloadType>
            className={className}
            formatLabel={formatTitle}
            formatValue={formatValue}
            showLabel={showLabel}
            translucent={translucent}
          >
            {children}
          </TooltipInner>
        }
        {...props}
      />
    )
  }
}

export function EmptyTooltip() {
  return null
}
